import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, MoreHorizontal, Package, Plus } from 'lucide-react';
import { formatCurrency, sumMoney, type NeedSummary, type User } from '@peles/shared';
import { EntityList, StatusBadge, type EntityListColumn } from '../common/EntityList';
import { EntityModal } from '../common/EntityModal';
import { DropdownMenu, type DropdownMenuItem } from '../common/DropdownMenu';
import { CreateExpensePanel } from '../expenses/CreateExpensePanel';
import { useEventNeeds } from '../../hooks/useNeeds';
import { useDialog } from '../../context/DialogContext';
import { useThreadCounts } from '../../hooks/useThreadCounts';
import { RecordConversations } from '../messaging/RecordConversations';
import { NeedSheet, isNeedSheetTab, type NeedSheetTab } from './NeedSheet';
import { BookNeedModal } from './BookNeedModal';
import { BudgetStrip } from './BudgetStrip';
import { describeQuotes, NEED_STAGE_COLORS, shortDay, stageLabel } from './needDisplay';

export interface NeedListProps {
  eventId: string;
  eventName: string;
  currentUser?: User;
}

/** Needs shown at once; a long event pages through them. */
export const NEEDS_PAGE_SIZE = 50;

const money = (n: number) => formatCurrency(n, { wholeDollars: true });

interface Group {
  categoryId: string;
  name: string;
  needs: NeedSummary[];
  budget: number;
  committed: number;
}

/** Needs grouped by category, in the order the server answered them. */
export function groupNeeds(needs: NeedSummary[]): Group[] {
  const groups = new Map<string, Group>();
  for (const need of needs) {
    let group = groups.get(need.vendorCategoryId);
    if (!group) {
      group = {
        categoryId: need.vendorCategoryId,
        name: need.vendorCategoryName,
        needs: [],
        budget: 0,
        committed: 0,
      };
      groups.set(need.vendorCategoryId, group);
    }
    group.needs.push(need);
  }
  for (const group of groups.values()) {
    group.budget = sumMoney(group.needs.map((n) => n.budgetAmount ?? 0));
    group.committed = sumMoney(group.needs.map((n) => n.booked?.price ?? 0));
  }
  return [...groups.values()];
}

/** What a need's row says after its name: the booking, or where its quotes stand. */
export function needProgress(need: NeedSummary): string {
  if (need.booked) {
    const b = need.booked;
    let owed = '';
    if (need.stage === 'PAID') owed = 'paid in full';
    else if (b.overdueAmount > 0) owed = `${money(b.overdueAmount)} overdue`;
    else if (b.nextDue?.date) {
      // The whole of what is left, or the next installment of a schedule.
      owed =
        b.nextDue.amount === b.balance
          ? `due ${shortDay(b.nextDue.date)}`
          : `next ${money(b.nextDue.amount)} ${shortDay(b.nextDue.date)}`;
    } else if (b.paid > 0) owed = `${money(b.balance)} left`;
    return [b.vendorName, money(b.price), owed].filter(Boolean).join(' · ');
  }
  const quotes = describeQuotes(need.quotes);
  if (!quotes)
    return need.itemCount > 0 ? `${need.itemCount} items · nothing asked yet` : 'Nothing asked yet';
  const lowest = need.quotes.lowestVendorName ? ` · lowest ${need.quotes.lowestVendorName}` : '';
  const waiting = need.quotes.filesWaiting > 0 ? ` · ${need.quotes.filesWaiting} file to read` : '';
  return `${quotes}${lowest}${waiting}`;
}

/**
 * The Plan view: every need of the event — sourcing and booked — by category,
 * each group's budget against what is committed. A need opens its sheet;
 * "Add a need" starts one to shop for.
 */
export function NeedList({ eventId, eventName, currentUser }: NeedListProps) {
  const { data, isLoading, isError, refetch } = useEventNeeds(eventId);
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState<'SOURCING' | 'BOOKED' | null>(null);
  const [booking, setBooking] = useState<NeedSummary | null>(null);
  const [dirty, setDirty] = useState(false);
  const { confirm } = useDialog();

  const needs = useMemo(() => data?.needs ?? [], [data]);
  const pageCount = Math.max(1, Math.ceil(needs.length / NEEDS_PAGE_SIZE));
  const shownPage = Math.min(page, pageCount);
  const groups = useMemo(
    () => groupNeeds(needs.slice((shownPage - 1) * NEEDS_PAGE_SIZE, shownPage * NEEDS_PAGE_SIZE)),
    [needs, shownPage],
  );

  /*
   * Every visible need's conversation badge in one request (COMMUNICATIONS-DESIGN
   * H3). A need is an expense line, so it is an `ExpenseItem` conversation — the
   * same one its sheet's Conversations tab opens; the row icon is the quick way in.
   */
  const { countFor: threadCountFor, refetch: refetchThreadCounts } = useThreadCounts(
    'ExpenseItem',
    groups.flatMap((group) => group.needs.map((need) => need.id)),
  );
  const onThreadActivity = useCallback(() => {
    void refetchThreadCounts();
  }, [refetchThreadCounts]);

  const openId = params.get('need');
  const openNeed = needs.find((n) => n.id === openId) ?? null;
  const tabParam = params.get('tab');
  const tab: NeedSheetTab = isNeedSheetTab(tabParam) ? tabParam : 'quotes';

  const openSheet = (need: NeedSummary | null, next: NeedSheetTab = 'quotes') => {
    setParams(
      (current) => {
        const p = new URLSearchParams(current);
        if (need) {
          p.set('need', need.id);
          p.set('tab', next);
        } else {
          p.delete('need');
          p.delete('tab');
        }
        return p;
      },
      { replace: !!need && need.id === openId },
    );
  };

  const closeAdd = async () => {
    if (
      dirty &&
      !(await confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      }))
    ) {
      return;
    }
    setDirty(false);
    setAdding(null);
  };

  const rowMenu = (need: NeedSummary): DropdownMenuItem[] => [
    {
      id: 'quotes',
      label: need.booked ? 'Quotes' : 'Compare quotes',
      onSelect: () => openSheet(need, 'quotes'),
    },
    ...(need.booked
      ? [{ id: 'money', label: 'Payments', onSelect: () => openSheet(need, 'money') }]
      : []),
    { id: 'items', label: 'Items', onSelect: () => openSheet(need, 'items') },
    { id: 'details', label: 'Details', onSelect: () => openSheet(need, 'details') },
    ...(need.booked
      ? []
      : [
          {
            id: 'book',
            label: 'Book directly',
            onSelect: () => setBooking(need),
            dividerBefore: true,
          },
        ]),
  ];

  const menu = (need: NeedSummary) => (
    <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
      <RecordConversations
        entityType="ExpenseItem"
        entityId={need.id}
        variant="icon"
        count={threadCountFor(need.id)}
        onActivity={onThreadActivity}
      />
      <DropdownMenu
        align="end"
        ariaLabel={`Actions for ${need.itemName || 'need'}`}
        items={rowMenu(need)}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={`Actions for ${need.itemName || 'need'}`}
            className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <MoreHorizontal size={16} />
          </button>
        )}
      />
    </div>
  );

  const columns: EntityListColumn<NeedSummary>[] = [
    {
      key: 'name',
      header: 'Need',
      render: (n) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-gray-900 truncate" title={n.itemName ?? undefined}>
            {n.itemName || 'Untitled need'}
          </span>
          <StatusBadge label={stageLabel(n.stage)} color={NEED_STAGE_COLORS[n.stage]} />
        </div>
      ),
    },
    {
      key: 'budget',
      header: 'Budget',
      align: 'right',
      width: 'w-32',
      render: (n) =>
        n.budgetAmount !== null ? money(n.budgetAmount) : <span className="text-gray-400">—</span>,
    },
    {
      key: 'progress',
      header: 'Where it stands',
      // Fixed widths, so every category's table lines its columns up with the next.
      width: 'w-[45%]',
      render: (n) => (
        <span className={n.booked && n.booked.overdueAmount > 0 ? 'text-red-600' : 'text-gray-600'}>
          {needProgress(n)}
        </span>
      ),
    },
    { key: 'actions', header: '', width: 'w-24', align: 'right', render: menu },
  ];

  const renderCard = (n: NeedSummary) => (
    <div className="p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{n.itemName || 'Untitled need'}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{n.vendorCategoryName}</span>
            <StatusBadge label={stageLabel(n.stage)} color={NEED_STAGE_COLORS[n.stage]} />
          </div>
        </div>
        {menu(n)}
      </div>
      {(n.budgetAmount !== null || n.itemCount > 0) && (
        <p className="text-sm text-gray-600">
          {[
            n.budgetAmount !== null ? `Budget ${money(n.budgetAmount)}` : null,
            n.itemCount ? `${n.itemCount} items` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <p className="text-sm text-gray-600">{needProgress(n)}</p>
    </div>
  );

  const totals = data?.totals;

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-start gap-3 justify-between">
        {totals && needs.length > 0 ? (
          <div className="flex-1">
            <BudgetStrip
              totals={totals}
              onShowExpenses={(cash) =>
                setParams((current) => {
                  const p = new URLSearchParams(current);
                  p.set('view', 'expenses');
                  p.set('cash', cash.length ? cash.join(',') : 'all');
                  p.delete('need');
                  p.delete('tab');
                  return p;
                })
              }
            />
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setAdding('SOURCING')}
            className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
          >
            <Plus size={16} /> Add a need
          </button>
          <DropdownMenu
            align="end"
            ariaLabel="More plan actions"
            items={[
              { id: 'book', label: 'Book a vendor directly', onSelect: () => setAdding('BOOKED') },
              {
                id: 'items',
                label: 'Items',
                icon: <Package size={14} />,
                onSelect: () =>
                  setParams((current) => {
                    const p = new URLSearchParams(current);
                    p.set('view', 'items');
                    p.delete('need');
                    p.delete('tab');
                    return p;
                  }),
              },
            ]}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <MoreHorizontal size={16} /> More
              </button>
            )}
          />
        </div>
      </div>

      {isError ? (
        <p className="text-sm text-red-600">
          The plan could not be loaded.{' '}
          <button
            type="button"
            className="underline min-h-11 sm:min-h-0"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </p>
      ) : isLoading ? (
        <EntityList items={[]} columns={columns} getKey={(n) => n.id} isLoading fixedLayout />
      ) : needs.length === 0 ? (
        <EntityList
          items={[]}
          columns={columns}
          getKey={(n) => n.id}
          emptyIcon={<ClipboardList size={40} className="text-gray-300" />}
          emptyMessage="Nothing planned yet. Add a need — flowers, a band, a photographer — to start asking for quotes."
        />
      ) : (
        groups.map((group) => (
          <section key={group.categoryId} aria-label={group.name}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {group.name}
              </h3>
              <p className="text-xs text-gray-500">
                {group.budget > 0 && `budget ${money(group.budget)}`}
                {group.budget > 0 && group.committed > 0 && ' · '}
                {group.committed > 0 && `committed ${money(group.committed)}`}
              </p>
            </div>
            <EntityList
              items={group.needs}
              columns={columns}
              getKey={(n) => n.id}
              onRowClick={(n) => openSheet(n)}
              renderCard={renderCard}
              selectedId={openId}
              fixedLayout
            />
          </section>
        ))
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
          <button
            type="button"
            disabled={shownPage === 1}
            onClick={() => setPage(shownPage - 1)}
            className="min-h-11 sm:min-h-8 px-3 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {shownPage} of {pageCount}
          </span>
          <button
            type="button"
            disabled={shownPage === pageCount}
            onClick={() => setPage(shownPage + 1)}
            className="min-h-11 sm:min-h-8 px-3 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      <EntityModal
        open={adding !== null}
        onClose={() => void closeAdd()}
        title={adding === 'BOOKED' ? 'Book a vendor directly' : 'Add a need'}
        size="lg"
      >
        {adding && (
          <CreateExpensePanel
            eventId={eventId}
            phase={adding}
            onClose={() => void closeAdd()}
            onCreated={() => {
              setDirty(false);
              setAdding(null);
            }}
            onUnsavedChange={setDirty}
          />
        )}
      </EntityModal>

      <NeedSheet
        eventId={eventId}
        eventName={eventName}
        need={openNeed}
        tab={tab}
        onTabChange={(next) => openNeed && openSheet(openNeed, next)}
        onClose={() => openSheet(null)}
        currentUser={currentUser}
      />
      <BookNeedModal need={booking} onClose={() => setBooking(null)} />
    </div>
  );
}
